/**
 * Canonical Notches entitlement registry.
 *
 * An "entitlement" is a string key identifying one product/feature a user has
 * access to. User records carry `entitlements: string[]` (NOT to be confused
 * with `subscriptions`, which is the legacy PayPal/Stripe billing record map).
 *
 * Purchase a tier  -> tier bundle materializes into the user's entitlements array
 * Buy individual   -> a single entitlement string is appended
 * Admin grant      -> any string from this registry can be added independently
 *
 * Decision: docs/wiki/decisions/2026-05-13-notches-product-umbrella-ia.md
 * Renamed from `subscriptions` to `entitlements` 2026-05-17 because the
 * `subscriptions` field name was already in use as the PayPal/Stripe billing
 * record map (user.subscriptions.spx = { paypalSubscriptionId, ... }).
 *
 * SOURCE OF TRUTH. The Python mirror (api/auth/src/entitlements.py) MUST
 * match. Future additions go in both places in one commit.
 *
 * CONSTRAINT: nothing in this file references legacy flat-flag names
 * (spxSignalsEnabled, notchesEnabled, etc.). The migration script bridges
 * those one-time; runtime code reads only entitlements.
 */

export const ENTITLEMENTS = {
  // Master grant - access to the Notches product umbrella at all
  NOTCHES: 'notches',

  // Preview / early-access gate. Used to ship new Notches surfaces (e.g. the
  // new Dashboard) to a closed set of users without affecting the rest of
  // the platform. Granted manually via DDB or admin tools.
  NOTCHES_PREVIEW: 'notches.preview',

  // Signals pillar
  SIGNALS: 'notches.signals',
  SIGNALS_OPTIONS: 'notches.signals.options',
  // Per-symbol options - each a separate paid product (2026-05-17: replaced
  // the INDEX + FUTURES bundle keys which over-granted access).
  SIGNALS_OPTIONS_SPX: 'notches.signals.options.spx',
  SIGNALS_OPTIONS_SPY: 'notches.signals.options.spy',
  SIGNALS_OPTIONS_QQQ: 'notches.signals.options.qqq',
  SIGNALS_OPTIONS_IWM: 'notches.signals.options.iwm',
  SIGNALS_OPTIONS_ES:  'notches.signals.options.es',
  SIGNALS_OPTIONS_MES: 'notches.signals.options.mes',
  SIGNALS_OPTIONS_GC:  'notches.signals.options.gc',
  SIGNALS_OPTIONS_CL:  'notches.signals.options.cl',
  SIGNALS_OPTIONS_NQ:  'notches.signals.options.nq',
  SIGNALS_OPTIONS_MNQ: 'notches.signals.options.mnq',
  // Bundled in the Premier tier
  SIGNALS_OPTIONS_EQUITY: 'notches.signals.options.equity',
  SIGNALS_STOCKS: 'notches.signals.stocks',
  SIGNALS_COMMODITIES: 'notches.signals.commodities',           // future

  // Markets pillar - editorial / commentary
  MARKETS: 'notches.markets',

  // Reports pillar - personalized report scheduling + download
  REPORTS: 'notches.reports',
  REPORTS_DAILY_DIGEST: 'notches.reports.daily-digest',
  REPORTS_WEEKLY: 'notches.reports.weekly',
  REPORTS_MONTHLY: 'notches.reports.monthly',
  REPORTS_IV: 'notches.reports.iv',
  REPORTS_VOICE: 'notches.reports.voice',

  // Auto-traders pillar
  AUTO_TRADERS: 'notches.auto-traders',
  AUTO_TRADERS_ALPACA: 'notches.auto-traders.alpaca',
  AUTO_TRADERS_TASTYTRADE: 'notches.auto-traders.tastytrade',
  AUTO_TRADERS_INTERACTIVE_BROKERS: 'notches.auto-traders.interactive-brokers',  // future

  // The Boring Bucket - mentored membership. Master gate for the in-app BB
  // section (Founder's Trades, Mentoring, Capital Plan).
  BORING_BUCKET: 'boring-bucket',
} as const;

export type EntitlementKey = typeof ENTITLEMENTS[keyof typeof ENTITLEMENTS];

/**
 * Complete list - used by admin UI dropdowns + validation + the migration
 * script's "is this a known key" check.
 */
export const ALL_ENTITLEMENTS: EntitlementKey[] = Object.values(ENTITLEMENTS);

/**
 * Human-readable labels for UI. Lookup: ENTITLEMENT_LABELS[ENTITLEMENTS.SIGNALS_STOCKS].
 */
export const ENTITLEMENT_LABELS: Record<EntitlementKey, string> = {
  [ENTITLEMENTS.NOTCHES]: 'Notches platform',
  [ENTITLEMENTS.NOTCHES_PREVIEW]: 'Notches Preview (early access)',
  [ENTITLEMENTS.SIGNALS]: 'All Signals',
  [ENTITLEMENTS.SIGNALS_OPTIONS]: 'All Options Signals',
  [ENTITLEMENTS.SIGNALS_OPTIONS_SPX]: 'Options Signals - SPX',
  [ENTITLEMENTS.SIGNALS_OPTIONS_SPY]: 'Options Signals - SPY',
  [ENTITLEMENTS.SIGNALS_OPTIONS_QQQ]: 'Options Signals - QQQ',
  [ENTITLEMENTS.SIGNALS_OPTIONS_IWM]: 'Options Signals - IWM',
  [ENTITLEMENTS.SIGNALS_OPTIONS_ES]:  'Options Signals - ES',
  [ENTITLEMENTS.SIGNALS_OPTIONS_MES]: 'Options Signals - MES',
  [ENTITLEMENTS.SIGNALS_OPTIONS_GC]:  'Options Signals - GC (Gold)',
  [ENTITLEMENTS.SIGNALS_OPTIONS_CL]:  'Options Signals - CL (Crude Oil)',
  [ENTITLEMENTS.SIGNALS_OPTIONS_NQ]:  'Options Signals - NQ (Nasdaq-100)',
  [ENTITLEMENTS.SIGNALS_OPTIONS_MNQ]: 'Options Signals - MNQ (Micro Nasdaq)',
  [ENTITLEMENTS.SIGNALS_OPTIONS_EQUITY]: 'Options Signals - Single-Stock',
  [ENTITLEMENTS.SIGNALS_STOCKS]: 'Stock Swing Signals',
  [ENTITLEMENTS.SIGNALS_COMMODITIES]: 'Commodity Signals',
  [ENTITLEMENTS.MARKETS]: 'Market Commentary',
  [ENTITLEMENTS.REPORTS]: 'All Reports',
  [ENTITLEMENTS.REPORTS_DAILY_DIGEST]: 'Daily Digest Report',
  [ENTITLEMENTS.REPORTS_WEEKLY]: 'Weekly Report',
  [ENTITLEMENTS.REPORTS_MONTHLY]: 'Monthly Report',
  [ENTITLEMENTS.REPORTS_IV]: 'IV Reports',
  [ENTITLEMENTS.REPORTS_VOICE]: 'Voice Reports',
  [ENTITLEMENTS.AUTO_TRADERS]: 'All Auto-Traders',
  [ENTITLEMENTS.AUTO_TRADERS_ALPACA]: 'Alpaca Auto-Trader',
  [ENTITLEMENTS.AUTO_TRADERS_TASTYTRADE]: 'TastyTrade Auto-Trader',
  [ENTITLEMENTS.AUTO_TRADERS_INTERACTIVE_BROKERS]: 'Interactive Brokers Auto-Trader',
  [ENTITLEMENTS.BORING_BUCKET]: 'The Boring Bucket (mentored)',
};

/**
 * Tier bundles. Buying a tier materializes its bundle into the user's
 * entitlements at issue time. The user record is the source of truth; tier
 * is the input. Admins can grant/revoke individual entitlements independent
 * of tier. Per-product purchases append a single entitlement string.
 */
// Confirmed 2026-05-17: only `free` and `premier` exist in production. Per-symbol
// options (SPX/SPY/ES/MES) and auto-traders are NEVER tier-bundled - they're
// individual paid products. IV Reports + Daily Report are free for all users
// (the dashboard renders them as always-active, no entitlement required).
export const TIER_BUNDLES: Record<string, EntitlementKey[]> = {
  free: [],

  premier: [
    ENTITLEMENTS.NOTCHES,
    ENTITLEMENTS.SIGNALS,
    ENTITLEMENTS.SIGNALS_OPTIONS,         // umbrella - user has at least equity
    ENTITLEMENTS.SIGNALS_OPTIONS_EQUITY,
    ENTITLEMENTS.SIGNALS_STOCKS,
  ],
};

/**
 * The Boring Bucket membership grants the full platform - every signal,
 * report, market, and auto-trader entitlement - plus the `boring-bucket`
 * section gate. $299/mo = full WFS access + mentoring + community + founder
 * transparency. Materialized by the Stripe webhook when the `boringBucket`
 * product is paid. Mirror of BORING_BUCKET_BUNDLE in api/auth/src/entitlements.js.
 */
export const BORING_BUCKET_BUNDLE: EntitlementKey[] = [
  ENTITLEMENTS.BORING_BUCKET,
  ENTITLEMENTS.NOTCHES,
  ENTITLEMENTS.SIGNALS,
  ENTITLEMENTS.SIGNALS_OPTIONS,
  ENTITLEMENTS.SIGNALS_OPTIONS_SPX,
  ENTITLEMENTS.SIGNALS_OPTIONS_SPY,
  ENTITLEMENTS.SIGNALS_OPTIONS_QQQ,
  ENTITLEMENTS.SIGNALS_OPTIONS_IWM,
  ENTITLEMENTS.SIGNALS_OPTIONS_ES,
  ENTITLEMENTS.SIGNALS_OPTIONS_MES,
  ENTITLEMENTS.SIGNALS_OPTIONS_GC,
  ENTITLEMENTS.SIGNALS_OPTIONS_CL,
  ENTITLEMENTS.SIGNALS_OPTIONS_EQUITY,
  ENTITLEMENTS.SIGNALS_STOCKS,
  ENTITLEMENTS.MARKETS,
  ENTITLEMENTS.REPORTS,
  ENTITLEMENTS.REPORTS_DAILY_DIGEST,
  ENTITLEMENTS.REPORTS_WEEKLY,
  ENTITLEMENTS.REPORTS_MONTHLY,
  ENTITLEMENTS.REPORTS_IV,
  ENTITLEMENTS.REPORTS_VOICE,
  ENTITLEMENTS.AUTO_TRADERS,
  ENTITLEMENTS.AUTO_TRADERS_ALPACA,
  ENTITLEMENTS.AUTO_TRADERS_TASTYTRADE,
];
